<script lang="ts" setup>
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import {computed, ref, useId} from 'vue';
import type {CdcDppFallbackMode} from '@/ts/composables/stateful/use-cdc-dpp-fallback';

// Program-completion fallback for an eligible user who is not funding access with insurance. The
// parent (useCdcDppEligibility) owns the state machine and mounts this with the current `mode`;
// each button maps to a semantic event the parent acts on. See docs/insurance-verification.
const props = defineProps<{
  mode: CdcDppFallbackMode;
  // The free-year grant is in flight: shows the accept buttons' busy state (double-click guard).
  granting?: boolean;
}>();

const emit = defineEmits<{
  grantYear: [];
  subscriberContinue: [];
  upgrade: [];
  declineToOffer: [];
  continueCovered: [];
  back: [];
  exit: [];
}>();

// A back-to-options X is shown on the per-type prompts so a user who picked "Continue" can return
// and try insurance instead. It is NOT shown on the free-year OFFER, which stays terminal — once
// the free year is revealed we don't bounce back to (and re-expose) the paid options.
const showBack = computed(() => props.mode !== 'OFFER_ONE_YEAR');

// Persistent dialog with no built-in close — it stays open until the parent unmounts it (v-if) in
// response to one of the emitted choices, so a local always-open flag is enough.
const isOpen = ref(true);
const headingId = useId();
</script>

<template>
  <balm-dialog
    v-model="isOpen"
    no-x
    persistent
    desktop-from-tablet
    no-scroll
    type="fit-content"
    theme="neutral-0"
    :aria-labelledby="headingId"
  >
    <div class="fallback-modal">
      <!-- Hidden while a grant is in flight (flat-icon-btn has no disable prop): navigating back
           mid-grant would re-expose the paid options while the free year lands underneath. -->
      <flat-icon-btn
        v-if="showBack && !granting"
        class="back-btn"
        icon="x-close.svg"
        :label="$t('cdc_dpp_back_aria')"
        @click="emit('back')"
      />
      <!-- New/trial: upgrade to complete the year, or leave the program (→ free-year offer). -->
      <template v-if="mode === 'UPGRADE_PROMPT'">
        <h2 :id="headingId" class="modal-heading">
          {{ $t('cdc_dpp_upgrade_heading') }}
        </h2>
        <p class="modal-body">
          {{ $t('cdc_dpp_upgrade_body') }}
        </p>
        <div class="modal-actions">
          <balm-btn :label="$t('cdc_dpp_upgrade_confirm')" @click="emit('upgrade')"/>
          <balm-btn :label="$t('cdc_dpp_leave_program')" theme="plain-underline" paddings="none" @click="emit('declineToOffer')"/>
        </div>
      </template>

      <!-- Paying user: keep their subscription, or leave the program (→ free-year offer). -->
      <template v-else-if="mode === 'SUBSCRIBER_CONTINUE'">
        <h2 :id="headingId" class="modal-heading">
          {{ $t('cdc_dpp_subscriber_continue_heading') }}
        </h2>
        <p class="modal-body">
          {{ $t('cdc_dpp_subscriber_continue_body') }}
        </p>
        <div class="modal-actions">
          <balm-btn :label="$t('cdc_dpp_subscriber_continue_confirm')" @click="emit('subscriberContinue')"/>
          <balm-btn :label="$t('cdc_dpp_leave_program')" theme="plain-underline" paddings="none" @click="emit('declineToOffer')"/>
        </div>
      </template>

      <!-- Voucher too short: inform we're granting a free year (Continue) or exit. -->
      <template v-else-if="mode === 'VOUCHER_GRANT'">
        <h2 :id="headingId" class="modal-heading">
          {{ $t('cdc_dpp_voucher_short_heading') }}
        </h2>
        <p class="modal-body">
          {{ $t('cdc_dpp_voucher_short_body') }}
        </p>
        <div class="modal-actions">
          <balm-btn :label="$t('cdc_dpp_voucher_short_accept')" :loading="granting" @click="emit('grantYear')"/>
          <balm-btn
            :label="$t('cdc_dpp_exit_program')"
            theme="plain-underline"
            paddings="none"
            :disable="granting"
            @click="emit('exit')"
          />
        </div>
      </template>

      <!-- Access already covers the programme: info + continue / exit (no offer). -->
      <template v-else-if="mode === 'COVERED_INFO'">
        <h2 :id="headingId" class="modal-heading">
          {{ $t('cdc_dpp_covered_heading') }}
        </h2>
        <p class="modal-body">
          {{ $t('cdc_dpp_covered_body') }}
        </p>
        <div class="modal-actions">
          <balm-btn :label="$t('cdc_dpp_continue_intervention')" @click="emit('continueCovered')"/>
          <balm-btn :label="$t('cdc_dpp_exit_program')" theme="plain-underline" paddings="none" @click="emit('exit')"/>
        </div>
      </template>

      <!-- Free-year save behind "leave": accept the free year, or exit (un-enroll). Terminal. -->
      <template v-else>
        <h2 :id="headingId" class="modal-heading">
          {{ $t('cdc_dpp_offer_year_heading') }}
        </h2>
        <p class="modal-body">
          {{ $t('cdc_dpp_offer_year_body') }}
        </p>
        <div class="modal-actions">
          <balm-btn :label="$t('cdc_dpp_offer_year_accept')" :loading="granting" @click="emit('grantYear')"/>
          <balm-btn
            :label="$t('cdc_dpp_exit_program')"
            theme="plain-underline"
            paddings="none"
            :disable="granting"
            @click="emit('exit')"
          />
        </div>
      </template>
    </div>
  </balm-dialog>
</template>

<style lang="scss" scoped>
.back-btn {
  position: absolute;
  top: var(--Spacing-spacing-3);
  right: var(--Spacing-spacing-3);
}

.fallback-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--Spacing-spacing-6);
  text-align: center;
  gap: var(--Spacing-spacing-4);
  max-height: 90dvh;
  overflow-y: auto;

  @include screen-md {
    min-width: 500px;
    padding: var(--Spacing-spacing-8) var(--Spacing-spacing-8) var(--Spacing-spacing-6);
  }
}

.modal-heading {
  @include font-display-4;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.modal-body {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
  text-align: left;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  width: 100%;

  // Primary is a full-width solid button; the secondary (plain-underline) stays a centered link.
  :deep(.balm-btn:not(.plain-underline)) {
    width: 100%;
  }
}
</style>
